import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property, query, queryAssignedNodes } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { customConverter } from '../../util/custom-converter';
import { isPopoverSupported } from '../../util/popover';
import { BlocksElement } from '../BlocksElement';
import { BlocksToast } from '../toast/toast';
import type { ToastAlignment, ToastVerticalPosition } from './toast-group.model';
import styles from './toast-group.styles';

/**
 * @slot default - Default slot in which to place toast
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksToastGroup extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Vertical position
   * 1) Adds the ability to open toast group vertically top and bottom to the page
   */
  @property({ type: String })
  verticalPosition: ToastVerticalPosition;

  /**
   * Horizontal position
   * 1) Adds the ability to open toast group horizontally left, right and center to the page
   */
  @property({ type: String })
  horizontalPosition: ToastAlignment;

  /**
   * Determines the behavior of the panel based on the value of the `isInline` property.
   * If `isInline` is false and the popover attribute is supported by the browser,
   * the toast group will be opened inside the top layer.
   * If `isInline` is true, the popover attribute will not be used, and the toast group
   * will use the floating UI absolute strategy.
   *  @property {Boolean} isInline - Default is false.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * @default true , controls whether focus should return back to the trigger once all the toast in the stack are closed
   */
  @property({
    // Allow "true|false" attribute values but keep the property boolean
    type: Boolean,
    converter: customConverter()
  })
  returnFocusToTrigger = true;

  @query('.cat-c-group-toast') private toastElement: HTMLElement | undefined;

  /**
   * query all slotted toast
   */
  @queryAssignedNodes({ flatten: true })
  private toasts: Array<BlocksToast> | undefined;

  private isPopoverSupported = isPopoverSupported();

  private previouslyActiveElement?: HTMLElement | null;

  private get isPopoverEnabled(): boolean | undefined {
    return (this.verticalPosition || this.horizontalPosition) && this.isPopoverSupported && !this.isInline;
  }

  protected firstUpdated() {
    if (this.isPopoverEnabled) {
      this.toastElement?.showPopover();
    }
  }
  /**
   * Close all toaster belong to the current toast-group
   */
  public closeAll() {
    this.toasts?.forEach((toast) => {
      toast.close?.();
    });
  }

  /**
   * Set the trigger element
   */
  private setTriggerElement() {
    this.previouslyActiveElement = document.activeElement as HTMLElement;
  }

  /**
   * If all the toasts are closed, then focus the trigger element if there is any
   */
  private focusTriggerElement() {
    const isAllToastDismissed = this.toasts?.every((item: BlocksToast) => item.isDismissed === true);

    if (isAllToastDismissed && this.previouslyActiveElement && this.returnFocusToTrigger) {
      this.previouslyActiveElement.focus();
    }
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-group-toast', {
      'cat-c-toast--pos-top': this.verticalPosition === 'top',
      'cat-c-toast--pos-bottom': this.verticalPosition === 'bottom',
      'cat-c-toast--pos-left': this.horizontalPosition === 'left',
      'cat-c-toast--pos-right': this.horizontalPosition === 'right',
      'cat-c-toast--pos-center': this.horizontalPosition === 'center'
    });

    return html`
      <div
        part="base"
        class="${componentClassNames}"
        role="log"
        aria-relevant="additions"
        popover=${ifDefined(this.isPopoverEnabled ? 'manual' : undefined)}
      >
        <slot part="part-default" @bl-toast-open=${this.setTriggerElement} @bl-toast-close=${this.focusTriggerElement}></slot>
      </div>
    `;
  }
}

if (customElements.get('cat-toast-group') === undefined) {
  customElements.define('cat-toast-group', BlocksToastGroup);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-toast-group': BlocksToastGroup;
  }
}
